import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./Games.css";

function Tables() {
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [categoryImages, setCategoryImages] = useState({});
    const [sortOption, setSortOption] = useState("name-asc");
    const [showNSFW, setShowNSFW] = useState(false);
    const [showDiscontinued, setShowDiscontinued] = useState(false); // NEW
    const [showCommunityOnly, setShowCommunityOnly] = useState(false);
    const [steamUser, setSteamUser] = useState(null);
    const [steamLibrary, setSteamLibrary] = useState([]);
    const [isSteamLoginInitiated, setIsSteamLoginInitiated] = useState(false);
    const adRef = useRef(null);
    localStorage.setItem("redirectAfterLogin", window.location.pathname);
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get("/api/admin/categories");
                const official = Array.isArray(response.data) ? response.data : [];

                official.forEach((category) => {
                    if (category.appid) {
                        fetchCategoryLogo(parseInt(category.appid, 10), category._id);
                    }
                });

                const commRes = await axios.get("/api/community/categories", { params: { status: "approved" } });
                const communityRaw = Array.isArray(commRes.data) ? commRes.data : [];

                const communityApproved = communityRaw.filter(
                    (c) => Array.isArray(c.tables) && c.tables.some((t) => t.status === "approved")
                );

                const officialKeys = new Set(
                    official.map((c) => (c.slug ? c.slug : String(c.appid || c.name).toLowerCase()))
                );

                const communityOnly = communityApproved
                    .filter((c) => {
                        const key = c.slug ? c.slug : String(c.appid || c.name).toLowerCase();
                        return !officialKeys.has(key);
                    })
                    .map((c) => ({ ...c, __isCommunityOnly: true }));

                communityOnly.forEach((c) => {
                    if (c.appid) {
                        fetchCategoryLogo(parseInt(c.appid, 10), c._id);
                    }
                });

                setCategories([...official, ...communityOnly]);
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setLoading(false);
            }
        };

        // Steam AppID → custom header image
        const CUSTOM_CATEGORY_IMAGES = {
            // Game has broken / missing Steam header
            "2623190":
            "https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/2623190/a7cee9165bb1bfc092c390c5cff215ce0e381dfc/header.jpg",

            // Hytale not on steam
            "4275070":
            "https://sintrix.net/hytale.jpg",

        };

        const fetchCategoryLogo = async (steamAppId, categoryId) => {
        // 1️⃣ Custom override first
        const customImage = CUSTOM_CATEGORY_IMAGES[Number(steamAppId)];
        if (customImage) {
            const img = new Image();
            img.src = customImage;

            img.onload = () => {
            setCategoryImages((prev) => ({ ...prev, [categoryId]: customImage }));
            };

            img.onerror = () => {
            setCategoryImages((prev) => ({ ...prev, [categoryId]: "default-placeholder.jpg" }));
            };

            return;
        }

        // 2️⃣ Standard Steam Fastly header
        let imageUrl = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${steamAppId}/header.jpg`;
        const img = new Image();
        img.src = imageUrl;

        img.onload = () => {
            setCategoryImages((prev) => ({ ...prev, [categoryId]: imageUrl }));
        };

        img.onerror = async () => {
            try {
            // 3️⃣ Backend fallback
            const response = await axios.get(`/api/steam/appdetails/${steamAppId}`);

            if (response.data?.header_image) {
                imageUrl = response.data.header_image;
                const retryImg = new Image();
                retryImg.src = imageUrl;

                retryImg.onload = () => {
                setCategoryImages((prev) => ({ ...prev, [categoryId]: imageUrl }));
                };

                retryImg.onerror = () => {
                setCategoryImages((prev) => ({ ...prev, [categoryId]: "default-placeholder.jpg" }));
                };
            } else {
                setCategoryImages((prev) => ({ ...prev, [categoryId]: "default-placeholder.jpg" }));
            }
            } catch (error) {
            console.error(`Error fetching image for appid ${steamAppId}:`, error);
            setCategoryImages((prev) => ({ ...prev, [categoryId]: "default-placeholder.jpg" }));
            }
        };
        };


        const fetchNSFWPreference = async () => {
            const token = localStorage.getItem("token");
            if (token) {
                try {
                    const preferencesResponse = await axios.get("/api/auth/preferences/nsfw", {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (preferencesResponse.data && preferencesResponse.data.showNSFW !== undefined) {
                        setShowNSFW(preferencesResponse.data.showNSFW);
                        sessionStorage.setItem("showNSFW", preferencesResponse.data.showNSFW);
                    }
                } catch (err) {
                    console.error("Error fetching NSFW preference:", err);
                }
            } else {
                const savedNSFW = sessionStorage.getItem("showNSFW");
                if (savedNSFW !== null) {
                    setShowNSFW(JSON.parse(savedNSFW));
                }
            }

            // Try to restore Discontinued preference from session (optional convenience)
            const savedDiscontinued = sessionStorage.getItem("showDiscontinued");
            if (savedDiscontinued !== null) {
                try {
                    setShowDiscontinued(JSON.parse(savedDiscontinued));
                } catch {
                    // ignore bad parse
                }
            }
        };

        fetchCategories();
        fetchNSFWPreference();
    }, []);

    useEffect(() => {
    const optedIn = sessionStorage.getItem("steamOptIn") === "1";
    if (optedIn) setIsSteamLoginInitiated(true);
    }, []);


    useEffect(() => {
    const fetchSteamUser = async () => {
        const assertJsonObject = (data, label) => {
        if (!data || typeof data !== "object") {
            throw new Error(`${label} did not return JSON (likely got index.html fallback)`);
        }
        };

        try {
        const userResponse = await axios.get("/api/steam/steam/user");
        assertJsonObject(userResponse.data, "Steam user");
        setSteamUser(userResponse.data);

        const libraryResponse = await axios.get("/api/steam/steam/library");
        assertJsonObject(libraryResponse.data, "Steam library");
        setSteamLibrary(libraryResponse.data.library || []);
        } catch (err) {
        console.error("Error fetching Steam user or library:", err);
        setSteamUser(null);
        setSteamLibrary([]);
        }
    };

    if (isSteamLoginInitiated && !steamUser) {
        fetchSteamUser();
    }
    }, [isSteamLoginInitiated, steamUser]);

    const location = useLocation();

    useEffect(() => {
        const initializeAds = () => {
            if (adRef.current) {
                if (!adRef.current.hasAttribute('data-adsbygoogle-status')) {
                    adRef.current.innerHTML = '';
                    try {
                        (window.adsbygoogle = window.adsbygoogle || []).push({});
                    } catch (e) {
                        console.error("AdSense push error:", e);
                    }
                }
            }
        };

        initializeAds();
        const timer = setTimeout(() => {
            initializeAds();
        }, 1000);

        return () => clearTimeout(timer);
    }, [location.pathname]);

    // Helper: detect if a category is discontinued
    const isCategoryDiscontinued = (c) => {
        const status = typeof c?.status === "string" ? c.status.toLowerCase() : "";
        return Boolean(
            c?.discontinued === true || c?.isDiscontinued === true || status === "discontinued"
        );
    };

    useEffect(() => {
        let filtered = categories.filter((category) =>
            (category.name || "").toLowerCase().includes(searchQuery.toLowerCase())
        );

        // NSFW filter
        if (!showNSFW) {
            filtered = filtered.filter((category) => !category.isNSFW);
        }

        // Discontinued filter (hide by default unless toggled on)
        if (!showDiscontinued) {
            filtered = filtered.filter((category) => !isCategoryDiscontinued(category));
        }

        // Steam library filter
        if (Array.isArray(steamLibrary) && steamLibrary.length > 0) {
            const ownedAppIds = steamLibrary.map((game) => String(game.appid));
            filtered = filtered.filter((category) => ownedAppIds.includes(String(category.appid)));
        }

        // Community-only filter
        if (showCommunityOnly) {
            filtered = filtered.filter((c) => c.__isCommunityOnly === true);
        }

        // Sort
        if (sortOption.startsWith("name")) {
            filtered.sort((a, b) =>
                sortOption === "name-asc"
                    ? (a.name || "").localeCompare(b.name || "")
                    : (b.name || "").localeCompare(a.name || "")
            );
        } else if (sortOption.startsWith("date")) {
            filtered.sort((a, b) =>
                sortOption === "date-newest"
                    ? new Date(b.createdAt) - new Date(a.createdAt)
                    : new Date(a.createdAt) - new Date(b.createdAt)
            );
        }

        setFilteredCategories(filtered);
    }, [categories, searchQuery, sortOption, showNSFW, showDiscontinued, steamLibrary, showCommunityOnly]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredCategories]);

    const indexOfLastCategory = currentPage * itemsPerPage;
    const indexOfFirstCategory = indexOfLastCategory - itemsPerPage;
    const currentCategories = filteredCategories.slice(indexOfFirstCategory, indexOfLastCategory);
    const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

    const handleCategoryClick = (category) => {
        const slug = category.slug || (category.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
        if (category.__isCommunityOnly) {
            navigate(`/community-tables/${slug}`);
        } else {
            navigate(`/game/${slug}`);
        }
    };

    const handleSteamLogin = () => {
    sessionStorage.setItem("steamOptIn", "1"); // <-- persists across refresh/return
    setIsSteamLoginInitiated(true);
    window.location.href = "/api/steam/auth/steam";
    };

    const handleNSFWToggle = async (isChecked) => {
        setShowNSFW(isChecked);
        sessionStorage.setItem("showNSFW", isChecked);
        const token = localStorage.getItem("token");
        if (token) {
            try {
                await axios.post(
                    "/api/auth/preferences/nsfw",
                    { showNSFW: isChecked },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
            } catch (error) {
                console.error("Error updating NSFW preference:", error);
            }
        }
    };

    const handleDiscontinuedToggle = (isChecked) => {
        setShowDiscontinued(isChecked);
        sessionStorage.setItem("showDiscontinued", isChecked);
    };

    const handleLogout = async () => {
    try {
        await axios.get("/api/steam/auth/steam/logout");
    } catch (err) {
        console.error("Error during logout:", err);
    } finally {
        sessionStorage.removeItem("steamOptIn"); // <-- important
        setSteamUser(null);
        setSteamLibrary([]);
        setIsSteamLoginInitiated(false);
    }
    };

    return (
        <div className="tables-page-container">
            <Helmet>
                <title>Supported Games and Cheat Tables</title>
                <meta
                    name="description"
                    content={categories.map((cat) => `${cat.name} cheat table`).join(", ")}
                />
            </Helmet>

            <h2 className="tables-title">Supported Games</h2>

            <div className="ad-container">
                <ins
                    ref={adRef}
                    key={location.pathname}
                    className="adsbygoogle"
                    style={{ display: "block" }}
                    data-ad-client="ca-pub-3980892685014254"
                    data-ad-slot="5067767497"
                    data-ad-format="auto"
                    data-full-width-responsive="true"
                />
            </div>

            <div className="steam-auth-section">
                {steamUser ? (
                    <div className="steam-user-info">
                        <img
                            src={steamUser.avatar}
                            alt="Steam Avatar"
                            className="steam-avatar"
                        />
                        <p>Welcome, {steamUser.username}</p>
                        <button onClick={handleLogout} className="logout-button">
                            Logout
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={handleSteamLogin}
                        className="steam-login-button"
                        title="Sign in with Steam to find cheat tables from your library. No data is stored—everything stays in your session."
                    >
                        <img
                            src="https://upload.wikimedia.org/wikipedia/commons/8/83/Steam_icon_logo.svg"
                            alt="Steam Logo"
                            className="steam-logo"
                        />
                        Sign in with Steam
                    </button>
                )}
            </div>

            <div className="search-sort-container">
                <input
                    type="text"
                    placeholder="Search games..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                />
                <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="sort-select"
                >
                    <option value="name-asc">Sort by Name (A-Z)</option>
                    <option value="name-desc">Sort by Name (Z-A)</option>
                    <option value="date-newest">Sort by Date Added (Newest to Oldest)</option>
                    <option value="date-oldest">Sort by Date Added (Oldest to Newest)</option>
                </select>
                <div className="toggles-row">
                    {/* NEW: Discontinued toggle (left of NSFW) */}
                    <div className="discontinued-toggle">
                        <label htmlFor="discontinued-filter" className="toggle-label">
                            <span className="toggle-text">Discontinued</span>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    id="discontinued-filter"
                                    className="toggle-input"
                                    checked={showDiscontinued}
                                    onChange={(e) => handleDiscontinuedToggle(e.target.checked)}
                                />
                                <span className="slider"></span>
                            </div>
                        </label>
                    </div>

                    <div className="nsfw-toggle">
                        <label htmlFor="nsfw-filter" className="toggle-label">
                            <span className="toggle-text">NSFW Games</span>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    id="nsfw-filter"
                                    className="toggle-input"
                                    checked={showNSFW}
                                    onChange={(e) => handleNSFWToggle(e.target.checked)}
                                />
                                <span className="slider"></span>
                            </div>
                        </label>
                    </div>

                    <div className="community-toggle">
                        <label htmlFor="community-filter" className="toggle-label">
                            <span className="toggle-text">Community</span>
                            <div className="toggle-switch">
                                <input
                                    type="checkbox"
                                    id="community-filter"
                                    className="toggle-input"
                                    checked={showCommunityOnly}
                                    onChange={(e) => setShowCommunityOnly(e.target.checked)}
                                />
                                <span className="slider"></span>
                            </div>
                        </label>
                    </div>
                </div>
            </div>

            <div
                className="contribute-callout"
                style={{
                    background: "var(--section-background)",
                    border: "1px solid var(--border-color)",
                    borderRadius: 12,
                    padding: "12px 16px",
                    margin: "10px 0 20px",
                    textAlign: "center"
                }}
            >
                Want to contribute a table?{" "}
                <a className="link" href="/community-tables">Click here to upload.</a>
            </div>

            {loading && <p>Loading categories...</p>}

            <div className="game-grid">
            {currentCategories.map((category) => {
            const isCommunity = category.__isCommunityOnly === true;
            const isDiscontinued = !isCommunity && isCategoryDiscontinued(category); // official-only

            const slug =
                category.slug ||
                (category.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

            const href = isCommunity ? `/community-tables/${slug}` : `/game/${slug}`;

            return (
                <div className="game-card" key={category._id}>
                {/* Top-right badges */}
                <div className="badge-row">
                    {isCommunity ? (
                    <span className="badge-pill badge-community" title="Community table">
                        Community
                    </span>
                    ) : (
                    <>
                        <span className="badge-pill badge-official" title="Official table">
                        Official
                        </span>
                        {isDiscontinued && (
                        <span
                            className="badge-pill badge-discontinued"
                            title="This official table is discontinued"
                        >
                            Discontinued
                        </span>
                        )}
                    </>
                    )}
                </div>

                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="game-link"
                >
                    <img
                    src={categoryImages[category._id] || "default-placeholder.jpg"}
                    alt={`${category.name} cheat table`}
                    className="game-image"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "default-placeholder.jpg";
                    }}
                    />
                    <div className="game-details">
                    <h3 className="game-name">{category.name}</h3>
                    </div>
                </a>
                </div>
            );
            })}
            </div>


            {!loading && totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                        <button
                            key={index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                            className={currentPage === index + 1 ? "active" : ""}
                        >
                            {index + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

export default Tables;
